/**
 * LinksChannelView - HUB Links channel
 *
 * Two tabs:
 * - Sent: SharedLink records (outgoing shares via the Share function)
 * - Received: SavedLink records (URLs saved from emails/messages/chat)
 *
 * Each link card has avatar, inner content, and fast actions.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Link2,
  ExternalLink,
  Trash2,
  Loader2,
  Copy,
  Check,
  Eye,
  MessageSquare,
  Edit3,
  Share2,
  ArrowUpRight,
  ArrowDownLeft,
  FolderKanban,
  FileText,
  CheckCircle2,
  Ban,
  Clock,
  Mail,
  MessageCircle,
  Globe,
  Bookmark,
  Users,
} from 'lucide-react';
import { useTranslation } from '../../hooks';
import { useSavedLinks, useDeleteSavedLink } from '@business-assistant/shared';
import { graphqlFetch } from '@business-assistant/shared/database/graphqlClient';
import type { SavedLink, LinkAssociation } from '@business-assistant/shared/domain';
import { useAppShell } from '../../layouts/AppShell';

// =============================================================================
// SHARED LINK TYPE (from GraphQL)
// =============================================================================

interface SharedLinkItem {
  id: string;
  resourceType: string;    // task | file | project | milestone
  resourceId: string;
  token: string;
  accessLevel: string;     // viewer | commenter | editor
  createdBy: string;
  recipientEmail?: string;
  expiresAt?: string;
  maxUses?: number;
  useCount: number;
  isActive: boolean;
  createdAt: string;
}

// =============================================================================
// GRAPHQL QUERIES
// =============================================================================

const ALL_SHARED_LINKS_QUERY = `
  query GetAllSharedLinks {
    allSharedLinks {
      id
      resourceType
      resourceId
      token
      accessLevel
      createdBy
      recipientEmail
      expiresAt
      maxUses
      useCount
      isActive
      createdAt
    }
  }
`;

const REVOKE_SHARED_LINK_MUTATION = `
  mutation RevokeSharedLink($id: ID!) {
    revokeSharedLink(id: $id)
  }
`;

const DELETE_SHARED_LINK_MUTATION = `
  mutation DeleteSharedLink($id: ID!) {
    deleteSharedLink(id: $id)
  }
`;

// =============================================================================
// PROPS
// =============================================================================

interface LinksChannelViewProps {
  onBack: () => void;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function LinksChannelView({ onBack }: LinksChannelViewProps) {
  const { t } = useTranslation();
  const { showToast } = useAppShell();

  // React Query hooks for saved links
  const { data: savedLinksData = [] } = useSavedLinks();
  const deleteSavedLink = useDeleteSavedLink();

  // Local UI state
  const [activeTab, setActiveTab] = useState<'sent' | 'received'>('sent');
  const [searchQuery, setSearchQuery] = useState('');

  // Sent links (SharedLink from GraphQL)
  const [sentLinks, setSentLinks] = useState<SharedLinkItem[]>([]);
  const [loadingSent, setLoadingSent] = useState(true);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Load sent links
  useEffect(() => {
    loadSentLinks();
  }, []);

  const loadSentLinks = useCallback(async () => {
    setLoadingSent(true);
    try {
      const result = await graphqlFetch<{ allSharedLinks: SharedLinkItem[] }>(ALL_SHARED_LINKS_QUERY);
      if (result.data?.allSharedLinks) {
        setSentLinks(result.data.allSharedLinks);
      }
    } catch (error) {
      console.error('[LinksChannelView] Failed to load shared links:', error);
    } finally {
      setLoadingSent(false);
    }
  }, []);

  // Filter sent links by search
  const filteredSent = useMemo(() => {
    if (!searchQuery.trim()) return sentLinks;

    const q = searchQuery.toLowerCase();
    return sentLinks.filter((l) =>
      l.resourceType.toLowerCase().includes(q) ||
      l.resourceId.toLowerCase().includes(q) ||
      l.token.toLowerCase().includes(q) ||
      l.recipientEmail?.toLowerCase().includes(q) ||
      l.accessLevel.toLowerCase().includes(q)
    );
  }, [sentLinks, searchQuery]);

  // Filter received links by search
  const filteredReceived = useMemo(() => {
    if (!searchQuery.trim()) return savedLinksData;

    const q = searchQuery.toLowerCase();
    return savedLinksData.filter((l) =>
      l.title.toLowerCase().includes(q) ||
      l.url.toLowerCase().includes(q) ||
      l.domain.toLowerCase().includes(q) ||
      l.description?.toLowerCase().includes(q)
    );
  }, [savedLinksData, searchQuery]);

  // Copy link to clipboard
  const handleCopyLink = useCallback(async (token: string) => {
    const url = `${window.location.origin}/share/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    }
  }, []);

  // Revoke shared link
  const handleRevoke = useCallback(async (id: string) => {
    try {
      const result = await graphqlFetch<{ revokeSharedLink: boolean }>(REVOKE_SHARED_LINK_MUTATION, { id });
      if (result.data?.revokeSharedLink) {
        setSentLinks((prev) => prev.map((l) => l.id === id ? { ...l, isActive: false } : l));
        showToast?.(t('hub.links.linkRevoked'), 'success');
      }
    } catch {
      showToast?.(t('common.error'), 'error');
    }
  }, [showToast, t]);

  // Delete shared link
  const handleDeleteSent = useCallback(async (id: string) => {
    try {
      const result = await graphqlFetch<{ deleteSharedLink: boolean }>(DELETE_SHARED_LINK_MUTATION, { id });
      if (result.data?.deleteSharedLink) {
        setSentLinks((prev) => prev.filter((l) => l.id !== id));
        showToast?.(t('hub.links.removedSuccessfully'), 'success');
      }
    } catch {
      showToast?.(t('common.error'), 'error');
    }
  }, [showToast, t]);

  // Remove received link
  const handleRemoveReceived = useCallback((id: string) => {
    deleteSavedLink.mutate(id, {
      onSuccess: () => {
        showToast?.(t('hub.links.removedSuccessfully'), 'success');
      },
      onError: () => {
        showToast?.(t('common.error'), 'error');
      },
    });
  }, [deleteSavedLink, showToast, t]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flex: 1,
          background: 'var(--bg-secondary)',
          borderRadius: '0.5rem',
          padding: '0.5rem 0.75rem',
        }}>
          <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('hub.links.searchPlaceholder')}
            style={{
              border: 'none',
              background: 'transparent',
              outline: 'none',
              flex: 1,
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
            }}
          />
        </div>

        {/* Tab switcher */}
        <div style={{
          display: 'flex',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          border: '1px solid var(--border-color)',
          flexShrink: 0,
        }}>
          <TabButton
            active={activeTab === 'sent'}
            onClick={() => setActiveTab('sent')}
            label={t('hub.links.sent')}
            count={filteredSent.length}
            icon={<ArrowUpRight size={13} />}
          />
          <TabButton
            active={activeTab === 'received'}
            onClick={() => setActiveTab('received')}
            label={t('hub.links.received')}
            count={filteredReceived.length}
            icon={<ArrowDownLeft size={13} />}
          />
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
        {activeTab === 'sent' ? (
          <SentTab
            links={filteredSent}
            loading={loadingSent}
            copiedToken={copiedToken}
            onCopy={handleCopyLink}
            onRevoke={handleRevoke}
            onDelete={handleDeleteSent}
            t={t}
          />
        ) : (
          <ReceivedTab
            links={filteredReceived}
            onRemove={handleRemoveReceived}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// TAB BUTTON
// =============================================================================

function TabButton({ active, onClick, label, count, icon }: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  icon?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '0.375rem 0.75rem',
        border: 'none',
        background: active ? 'var(--accent-color, #3b82f6)' : 'transparent',
        color: active ? '#fff' : 'var(--text-secondary)',
        fontSize: '0.8125rem',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0.375rem',
      }}
    >
      {icon}
      {label}
      {count > 0 && (
        <span style={{
          background: active ? 'rgba(255,255,255,0.25)' : 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          padding: '0.0625rem 0.375rem',
          fontSize: '0.6875rem',
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

// =============================================================================
// SENT TAB
// =============================================================================

function SentTab({ links, loading, copiedToken, onCopy, onRevoke, onDelete, t }: {
  links: SharedLinkItem[];
  loading: boolean;
  copiedToken: string | null;
  onCopy: (token: string) => void;
  onRevoke: (id: string) => void;
  onDelete: (id: string) => void;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
        <Share2 size={40} style={{ opacity: 0.4, marginBottom: '0.75rem' }} />
        <p>{t('hub.links.noSentLinks')}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {links.map((link) => (
        <SentLinkCard
          key={link.id}
          link={link}
          copied={copiedToken === link.token}
          onCopy={() => onCopy(link.token)}
          onRevoke={() => onRevoke(link.id)}
          onDelete={() => onDelete(link.id)}
          t={t}
        />
      ))}
    </div>
  );
}

function SentLinkCard({ link, copied, onCopy, onRevoke, onDelete, t }: {
  link: SharedLinkItem;
  copied: boolean;
  onCopy: () => void;
  onRevoke: () => void;
  onDelete: () => void;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  const resourceIcon = getResourceIcon(link.resourceType);
  const resourceColor = getResourceColor(link.resourceType);
  const accessIcon = getAccessIcon(link.accessLevel);
  const isExpired = link.expiresAt ? new Date(link.expiresAt) < new Date() : false;
  const inactive = !link.isActive || isExpired;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      padding: '0.75rem',
      borderRadius: '0.5rem',
      border: '1px solid var(--border-color)',
      background: 'var(--bg-primary)',
      opacity: inactive ? 0.6 : 1,
    }}>
      {/* Avatar / Resource icon */}
      <div style={{
        width: 40,
        height: 40,
        borderRadius: '0.5rem',
        background: `${resourceColor}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: resourceColor,
      }}>
        {resourceIcon}
      </div>

      {/* Inner content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.25rem' }}>
          <span style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
          }}>
            {t(`hub.links.resourceType.${link.resourceType}`)}
          </span>
          {/* Status badges */}
          <AccessBadge level={link.accessLevel} icon={accessIcon} />
          {inactive && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.125rem',
              padding: '0.0625rem 0.375rem',
              borderRadius: '0.25rem',
              fontSize: '0.625rem',
              fontWeight: 600,
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
            }}>
              {!link.isActive ? <Ban size={9} /> : <Clock size={9} />}
              {!link.isActive ? t('hub.links.revoked') : t('hub.links.expired')}
            </span>
          )}
        </div>

        {/* Details */}
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
          <span style={{ fontFamily: 'monospace', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            /share/{link.token}
          </span>
        </div>

        {/* Meta row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontSize: '0.6875rem',
          color: 'var(--text-muted)',
        }}>
          {link.recipientEmail && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Mail size={10} />
              {link.recipientEmail}
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Users size={10} />
            {t('hub.links.uses', { count: link.useCount })}
            {link.maxUses != null && ` / ${link.maxUses}`}
          </span>
          {link.expiresAt && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Clock size={10} />
              {new Date(link.expiresAt).toLocaleDateString()}
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {formatDate(link.createdAt)}
          </span>
        </div>
      </div>

      {/* Fast actions */}
      <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
        <ActionButton
          icon={copied ? <Check size={14} style={{ color: '#22c55e' }} /> : <Copy size={14} />}
          title={t('hub.links.copyLink')}
          onClick={onCopy}
          disabled={inactive}
        />
        {link.isActive && (
          <ActionButton
            icon={<Ban size={14} />}
            title={t('hub.links.revokeLink')}
            onClick={onRevoke}
          />
        )}
        <ActionButton
          icon={<Trash2 size={14} />}
          title={t('hub.links.remove')}
          onClick={onDelete}
          danger
        />
      </div>
    </div>
  );
}

// =============================================================================
// RECEIVED TAB
// =============================================================================

function ReceivedTab({ links, onRemove, t }: {
  links: SavedLink[];
  onRemove: (id: string) => void;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  if (links.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
        <Bookmark size={40} style={{ opacity: 0.4, marginBottom: '0.75rem' }} />
        <p>{t('hub.links.noReceivedLinks')}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {links.map((link) => (
        <ReceivedLinkCard key={link.id} link={link} onRemove={() => onRemove(link.id)} t={t} />
      ))}
    </div>
  );
}

function ReceivedLinkCard({ link, onRemove, t }: {
  link: SavedLink;
  onRemove: () => void;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      padding: '0.75rem',
      borderRadius: '0.5rem',
      border: '1px solid var(--border-color)',
      background: 'var(--bg-primary)',
    }}>
      {/* Avatar */}
      <div style={{
        width: 40,
        height: 40,
        borderRadius: '0.5rem',
        background: 'var(--bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Globe size={18} style={{ color: 'var(--text-muted)' }} />
      </div>

      {/* Inner content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'var(--text-primary)',
          marginBottom: '0.125rem',
        }}>
          {link.title}
        </div>
        {link.description && (
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            marginBottom: '0.25rem',
          }}>
            {link.description}
          </div>
        )}
        <div style={{
          fontSize: '0.75rem',
          color: 'var(--accent-color, #3b82f6)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          marginBottom: '0.25rem',
        }}>
          {link.domain} &mdash; {link.url}
        </div>

        {/* Associations */}
        {link.associations.length > 0 && (
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
            {link.associations.map((assoc) => (
              <AssociationChip key={assoc.entityId} assoc={assoc} />
            ))}
          </div>
        )}

        {/* Source */}
        <div style={{
          fontSize: '0.6875rem',
          color: 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}>
          {link.source.sourceType === 'email' ? <Mail size={10} /> : <MessageCircle size={10} />}
          {t('hub.links.from')}: {link.source.sourceLabel}
          <span style={{ marginLeft: '0.5rem' }}>{formatDate(link.savedAt)}</span>
        </div>
      </div>

      {/* Fast actions */}
      <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: '0.375rem',
            borderRadius: '0.25rem',
            border: '1px solid var(--border-color)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            display: 'flex',
            cursor: 'pointer',
            textDecoration: 'none',
          }}
          title={t('hub.links.openExternal')}
        >
          <ExternalLink size={14} />
        </a>
        <ActionButton
          icon={<Trash2 size={14} />}
          title={t('hub.links.remove')}
          onClick={onRemove}
          danger
        />
      </div>
    </div>
  );
}

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

function ActionButton({ icon, title, onClick, disabled, danger }: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '0.375rem',
        borderRadius: '0.25rem',
        border: '1px solid var(--border-color)',
        background: 'transparent',
        color: danger ? 'var(--text-muted)' : 'var(--text-secondary)',
        display: 'flex',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
      title={title}
    >
      {icon}
    </button>
  );
}

function AccessBadge({ level, icon }: { level: string; icon: React.ReactNode }) {
  const colorMap: Record<string, string> = {
    viewer: '#6b7280',
    commenter: '#f59e0b',
    editor: '#3b82f6',
  };
  const color = colorMap[level] || '#6b7280';

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.125rem',
      padding: '0.0625rem 0.375rem',
      borderRadius: '0.25rem',
      fontSize: '0.625rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      background: `${color}15`,
      color: color,
    }}>
      {icon}
      {level}
    </span>
  );
}

function AssociationChip({ assoc }: { assoc: LinkAssociation }) {
  const colorMap: Record<string, string> = {
    project: '#3b82f6',
    contact: '#10b981',
    event: '#f59e0b',
  };
  const color = colorMap[assoc.entityType] || '#6b7280';

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.125rem 0.5rem',
      borderRadius: '1rem',
      fontSize: '0.6875rem',
      fontWeight: 500,
      background: `${color}20`,
      color: color,
      border: `1px solid ${color}40`,
    }}>
      {assoc.confirmed && <CheckCircle2 size={10} />}
      {assoc.entityLabel}
    </span>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function getResourceIcon(type: string): React.ReactNode {
  switch (type) {
    case 'project': return <FolderKanban size={20} />;
    case 'task': return <CheckCircle2 size={20} />;
    case 'file': return <FileText size={20} />;
    case 'milestone': return <Link2 size={20} />;
    default: return <Share2 size={20} />;
  }
}

function getResourceColor(type: string): string {
  switch (type) {
    case 'project': return '#3b82f6';
    case 'task': return '#22c55e';
    case 'file': return '#f59e0b';
    case 'milestone': return '#8b5cf6';
    default: return '#6b7280';
  }
}

function getAccessIcon(level: string): React.ReactNode {
  switch (level) {
    case 'viewer': return <Eye size={9} />;
    case 'commenter': return <MessageSquare size={9} />;
    case 'editor': return <Edit3 size={9} />;
    default: return <Eye size={9} />;
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}
